import React, { useState, useEffect } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { platformLogout, setPlatformUser } from "../../app/slices/userSlice";
import { useBranding } from "../../hooks/useBranding";
import { getPlatformName, usePlatformSettings } from "../../hooks/usePlatformAdmin";
import { get, post } from "../../services/api";
import AppVersionLabel from "../../components/shared/AppVersionLabel";
import styles from "./PlatformLayout.module.css";

const platformNav = [
  {
    to: "/app",
    label: "Overview",
    description: "Health, counts, and recent platform activity.",
    end: true,
  },
  {
    to: "/app/marketing",
    label: "Marketing",
    description: "Public site, content, SEO, and campaign tools.",
  },
  {
    to: "/app/staff",
    label: "Admins",
    description: "Platform owners and operators.",
  },
  {
    to: "/app/users",
    label: "Users",
    description: "All registered platform accounts.",
  },
  {
    to: "/app/clans",
    label: "Clans",
    description: "Customer spaces, domains, and ownership.",
  },
  {
    to: "/app/vendors",
    label: "Vendors",
    description: "Providers inside the Uniflo marketplace.",
  },
  {
    to: "/app/products",
    label: "Products",
    description: "Multivendor product catalog and feature bundles.",
  },
  {
    to: "/app/plans",
    label: "Plans",
    description: "Pricing, billing cadence, and trials.",
  },
  {
    to: "/app/subscriptions",
    label: "Subscriptions",
    description: "Tenant package entitlements and lifecycle.",
  },
  {
    to: "/app/domains",
    label: "Domains",
    description: "CNAME routing and certificate checks.",
  },
  {
    to: "/app/logs",
    label: "System Logs",
    description: "Platform-wide requests, errors, actors, and sockets.",
  },
];

const DEFAULT_PLATFORM_LOGO_URL = "/uniflo-reference/logo.png";

function AccessBlocked({ platformName = "Uniflo" }) {
  return (
    <div className={styles.shell}>
      <div className={styles.blocked}>
        <div className={styles.eyebrow}>Platform Access Required</div>
        <h1>Platform control is separate from tenant admin.</h1>
        <p>
          Your account is signed in, but it does not currently carry a {platformName}
          platform role. Sign in with a platform owner or platform admin account to continue.
        </p>
        <div className={styles.blockedActions}>
          <Link className={styles.topbarLink} to="/account">
            Go To Tenant App
          </Link>
          <Link className={styles.topbarLink} to="/app/login">
            Sign In As Platform Staff
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function PlatformLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.user.platformUser);
  // When there's no client-side platform user (e.g. a fresh deep-link load, or a
  // browser that didn't surface the `platform-user` cookie), don't bounce to
  // login immediately — first verify the httpOnly jwt-platform session via the
  // server whoami and re-hydrate from it. Only redirect once that confirms there
  // is genuinely no session. This makes platform admin work in every browser.
  const [sessionChecked, setSessionChecked] = useState(Boolean(user));
  const [sessionMissing, setSessionMissing] = useState(false);

  useEffect(() => {
    if (user) {
      setSessionChecked(true);
      setSessionMissing(false);
      return undefined;
    }
    let cancelled = false;
    setSessionChecked(false);
    (async () => {
      try {
        const res = await get("/api/v1/platform/auth/me", {
          skipAuthRedirect: true,
        });
        const me = res?.data?.user || null;
        if (cancelled) return;
        if (me) {
          dispatch(setPlatformUser(me));
        } else {
          setSessionMissing(true);
        }
      } catch (_error) {
        if (!cancelled) setSessionMissing(true);
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, dispatch]);

  const hideTopbar = location.pathname === "/app/settings";
  const { data: platformSettings } = usePlatformSettings();
  const { platformSiteName, platformLogoUrl } = useBranding();
  const platformMark = platformSettings?.general?.logoUrl || platformLogoUrl || DEFAULT_PLATFORM_LOGO_URL;
  const platformName = platformSettings?.general?.platformName || platformSiteName || "Uniflo";

  if (!user) {
    // Still verifying the httpOnly session — hold (don't bounce) so a valid
    // admin on a fresh load / non-Chrome browser isn't wrongly redirected.
    if (!sessionChecked) {
      return (
        <div className={styles.shell}>
          <div className={styles.blocked}>
            <div className={styles.eyebrow}>{platformName}</div>
            <h1>Checking your platform session…</h1>
          </div>
        </div>
      );
    }
    // Whoami confirmed there is no platform session → go to login.
    if (sessionMissing) {
      return (
        <Navigate
          to="/app/login"
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }
    // Whoami succeeded; setPlatformUser dispatched — render once state updates.
    return null;
  }

  const handleLogout = async () => {
    try {
      await post("/api/v1/platform/auth/logout");
    } catch (_error) {
      // Best-effort logout
    }
    dispatch(platformLogout());
    navigate("/app/login", { replace: true });
  };

  return (
    <div className={styles.shell}>
      <div className={styles.frame}>
        <aside className={styles.sidebar}>
          <Link className={styles.brand} to="/app">
            <div className={styles.brandMark}>
              <img src={platformMark} alt={platformName} />
            </div>
            <div className={styles.brandText}>
              <div className={styles.brandEyebrow}>{platformName}</div>
              <div className={styles.brandName}>Platform Control</div>
            </div>
          </Link>

          <div className={styles.userCard}>
            <div className={styles.userName}>{getPlatformName(user)}</div>
            <div className={styles.userMeta}>{user.email}</div>
            <div className={styles.roleList}>
              {(user.platformRoles || []).map((role) => (
                <span className={styles.rolePill} key={role}>
                  {role.replaceAll("_", " ")}
                </span>
              ))}
            </div>
          </div>

          <nav className={styles.nav} aria-label="Platform navigation">
            {platformNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
                }
              >
                <span className={styles.navText}>
                  <span className={styles.navTitle}>{item.label}</span>
                  <span className={styles.navDescription}>{item.description}</span>
                </span>
                <span className={styles.navArrow}>↗</span>
              </NavLink>
            ))}
          </nav>

          <div className={styles.sidebarFooter}>
            <Link className={styles.homeButton} to="/app/settings">
              Platform Settings
            </Link>
            <button className={styles.logoutButton} type="button" onClick={handleLogout}>
              Sign Out
            </button>
            {/* Marketing version + GitHub build stamp of what is running. */}
            <AppVersionLabel className={styles.footerCopy} />
          </div>
        </aside>

        <main className={styles.content}>
          {hideTopbar ? null : (
            <div className={styles.topbar}>
              <div className={styles.topbarCopy} />
              <Link className={styles.topbarLink} to="/">
                View Marketing Site
              </Link>
            </div>
          )}

          <Outlet />
        </main>
      </div>
    </div>
  );
}
